import React, { useContext, useEffect, useState } from 'react'
import {
  Checkbox,
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  ITag,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Separator,
  Stack,
  TextField,
} from 'office-ui-fabric-react'
import { getAllAccountsQuery, Integration } from '../../Api/integrations'
import { IntegrationLimitingForm } from '../../Components/Input/IntegrationLimitingForm'
import isValidDomain from 'is-valid-domain'
import { SyncScript } from './SyncScript'
import { Seller } from '../../Api/schain'
import { Ownership } from './Ownership'
import { TagInput } from '../../Components/Input/TagInput'
import { useQuery } from '@apollo/react-hooks'
import { SessionContext } from '../../Contexts/SessionContext'
import { client } from '../../Api/api'
import InfoBox from '../CampaignCollectionScreen/CampaignDialog/InfoBox'

interface Props {
  disabled: boolean
  integration?: Integration
  onSubmit: (integration: Integration) => void
  onCancel: () => void
  error: string
  sellers: Seller[]
  isAdmin: boolean
}

const defaultIntegration: Integration = {
  publishedDate: '',
  domain: '',
  whitelist: [],
  blacklist: [],
  ownership: '',
  integratedWithInview: false,
  integratedWithR2B2: true,
  monetizedBy: [],
}

interface Error {
  field: string
  message: string
}

export function EditIntegrationDialog({
  disabled,
  integration = defaultIntegration,
  error,
  onSubmit,
  onCancel,
  sellers,
  isAdmin,
}: Props) {
  const [isNewIntegration] = useState(!integration.domain)
  const [errors, setErrors] = useState<Error[]>([])
  const [editIntegration, setEditIntegration] = useState<Integration>({
    ...integration,
  })
  const { data: allAccountsResponse, loading } = useQuery(getAllAccountsQuery, {
    fetchPolicy: 'cache-first',
    client,
  })
  const monetizedByTags: ITag[] = allAccountsResponse
    ? allAccountsResponse.allAccounts.map(({ account, displayName }: any) => ({
        key: account,
        name: displayName,
      }))
    : []
  const sessionContext = useContext(SessionContext)
  useEffect(() => {
    setEditIntegration({ ...integration })
  }, [integration])
  return (
    <Dialog
      hidden={false}
      modalProps={{
        isDarkOverlay: true,
        isBlocking: true,
      }}
      className="edit-integration-dialog"
      onDismiss={onCancel}
      dialogContentProps={{
        type: DialogType.largeHeader,
        title: !isNewIntegration
          ? `Edit ${integration.domain} Integration`
          : 'Create Integration',
      }}
    >
      <Stack gap={16}>
        {isNewIntegration && (
          <TextField
            disabled={disabled}
            label="Domain"
            value={editIntegration.domain}
            autoComplete="off"
            spellCheck={false}
            onChange={(_, domain = '') => {
              setEditIntegration({
                ...editIntegration,
                domain,
              })
            }}
            required
            errorMessage={getErrorMessage(errors, 'domain')}
          />
        )}
        <IntegrationLimitingForm
          disabled={disabled}
          errorMessages={{}}
          integrationLimits={{
            whitelistGlobs: editIntegration.whitelist,
            blacklistGlobs: editIntegration.blacklist,
          }}
          onChange={({
            whitelistGlobs: whitelist,
            blacklistGlobs: blacklist,
          }: any) => {
            setEditIntegration({
              ...editIntegration,
              whitelist: whitelist || editIntegration.whitelist,
              blacklist: blacklist || editIntegration.blacklist,
            })
          }}
        />
        {integration.syncScript && (
          <SyncScript syncScript={integration.syncScript} />
        )}
        <Ownership
          ownership={editIntegration.ownership}
          disabled={disabled}
          sellers={sellers}
          onUpdated={(ownership) => {
            setEditIntegration({
              ...editIntegration,
              ownership: ownership,
            })
          }}
        />
        {isAdmin &&
          !sessionContext.features.includes(
            'campaignsCollectionsSidebarFilter'
          ) && (
            <Checkbox
              label="Integrated With Inview"
              disabled={disabled}
              checked={editIntegration.integratedWithInview}
              onChange={(_, checked?: boolean) => {
                setEditIntegration({
                  ...editIntegration,
                  integratedWithInview: !!checked,
                })
              }}
              styles={{ root: { marginTop: 10 } }}
            />
          )}
        {isAdmin &&
          !sessionContext.features.includes(
            'campaignsCollectionsSidebarFilter'
          ) && (
            <Checkbox
              label="Integrated With R2B2"
              disabled={disabled}
              checked={editIntegration.integratedWithR2B2}
              onChange={(_, checked?: boolean) => {
                setEditIntegration({
                  ...editIntegration,
                  integratedWithR2B2: !!checked,
                })
              }}
              styles={{ root: { marginTop: 10 } }}
            />
          )}
        {isAdmin &&
          !sessionContext.features.includes(
            'allowCrossAccountIntegrations'
          ) && (
            <Stack gap={32}>
              <TagInput
                label="Also Monetized by"
                required={false}
                onResolveSuggestions={(
                  filter: string,
                  selectedTags?: ITag[]
                ) => {
                  const filterRank = (t: ITag) =>
                    t.name.toLowerCase().indexOf(filter.toLowerCase())
                  return monetizedByTags
                    .filter(
                      (t) =>
                        t.key !== sessionContext.accountName &&
                        filterRank(t) !== -1
                    )
                    .filter(
                      (t) =>
                        !(selectedTags || []).find((t2) => t2.key === t.key)
                    )
                    .sort((a, b) => filterRank(a) - filterRank(b))
                }}
                onChange={async (selectedTags?: ITag[]) => {
                  setEditIntegration({
                    ...editIntegration,
                    monetizedBy: selectedTags
                      ? selectedTags.map((t) => t.key)
                      : [],
                  })
                }}
                selectedItems={monetizedByTags.filter(({ key }) =>
                  editIntegration.monetizedBy.includes(key)
                )}
                disabled={loading || disabled}
                pickerLabels={{
                  noResults: '',
                  suggestionsHeader: '',
                }}
                itemLimit={9999}
              />
              <InfoBox>
                Placements from these accounts will be able to run on this
                domain. Tracking will be kept separate for each account. <br />
                This setting is only available for administrators.
              </InfoBox>
            </Stack>
          )}
      </Stack>
      <Separator />
      {error && (
        <MessageBar
          messageBarType={MessageBarType.error}
          isMultiline={true}
          styles={{ root: { marginTop: 15 } }}
        >
          {error}
        </MessageBar>
      )}
      <DialogFooter>
        <DefaultButton onClick={onCancel} disabled={disabled} text="Cancel" />
        <PrimaryButton
          onClick={() => {
            const errors = validate(editIntegration)
            setErrors(errors)
            if (!errors.length) {
              onSubmit(editIntegration)
            }
          }}
          disabled={disabled}
          text={!!integration ? 'Save' : 'Create'}
        />
      </DialogFooter>
    </Dialog>
  )
}

function validate(integration: Integration): Error[] {
  const errors: Error[] = []
  if (!integration.domain.trim()) {
    errors.push({ field: 'domain', message: 'domain cannot be empty' })
  } else if (!isValidDomain(integration.domain.trim())) {
    errors.push({ field: 'domain', message: 'this is not a valid domain' })
  }
  return errors
}

function getErrorMessage(errors: Error[], field: string) {
  const isSameField = (error: Error) => error.field === field
  const error = errors.find(isSameField)
  return error ? error.message : ''
}
